import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Label, Input, Card } from "@tellescope/design-system";
import { Button, LanguageSelector } from "@tellescope/ui";
import { useAuth } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { useOrganization } from "../../shared/store/organizationStore";

export const AdminLoginPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const organization = useOrganization();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { login, loginError: error, isAuthenticated, requiresMFA } = useAuth();

  // Redirect to dashboard if fully authenticated. Exclude the MFA-pending state
  // (isAuthenticated && requiresMFA) so that resetAuth() on MFA cancel — which
  // clears the token asynchronously — doesn't cause a bounce back to /admin/mfa.
  if (isAuthenticated && !requiresMFA) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);

    try {
      const result = await login({ email, password });
      if (result?.requiresMFA) {
        navigate("/admin/mfa", { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8 relative">
      {/* Language selector in top-right corner */}
      <div className="absolute top-4 right-4">
        <LanguageSelector size="sm" />
      </div>

      <Card variant="floating" padding="root" className="w-full max-w-md">
        <Card.Header className="text-center">
          <Card.Title className="text-2xl font-bold">{t("Admin Login")}</Card.Title>
          <Card.Description>
            {organization?.name
              ? t("Sign in to configure the portal for {{organizationName}}", { organizationName: organization.name })
              : t("Sign in to the portal admin panel")}
          </Card.Description>
        </Card.Header>
        <Card.Body>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("Email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("Password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                {typeof error === "string" ? error : error.message}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading || !email || !password}>
              {isLoading ? t("Signing in...") : t("Sign In")}
            </Button>
          </form>
        </Card.Body>
      </Card>
    </div>
  );
};
